import { useState } from "react";
import { toast } from "sonner";
import { Loader2, PhoneCall, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";

const CALENDLY_EMPLOYER_URL = "https://calendly.com/hello-theditchcollective/careers-call-employer?hide_gdpr_banner=1&primary_color=c1cc24";

const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";

export const BookCallDialog = ({ open, onOpenChange }) => {
  const [mode, setMode] = useState("calendly");
  const [form, setForm] = useState({ name: "", email: "", company_name: "", roles_per_year: "", contact_method: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setMode("calendly"); }}>
      <DialogContent className={`bg-[#fcfbf8] ${mode === "calendly" && !done ? "max-w-3xl" : "max-w-md"}`} data-testid="book-call-dialog">
        {done ? (
          <div className="py-8 text-center" data-testid="book-call-success">
            <CheckCircle2 size={40} className="mx-auto text-[#7C8514]" />
            <h3 className="font-heading font-bold text-[#0d2f4d] text-xl mt-4">Thanks — we've got it.</h3>
            <p className="text-sm text-[#51637a] mt-2 leading-relaxed">We'll be in touch within 1 business day to work out what's right for you.</p>
          </div>
        ) : mode === "calendly" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-[#0d2f4d]">Book a call — pick a time that suits</DialogTitle>
              <DialogDescription className="text-[#51637a]">15 minutes with a recruiter, no commitment.</DialogDescription>
            </DialogHeader>
            <iframe src={CALENDLY_EMPLOYER_URL} title="Book a call" data-testid="calendly-embed"
              className="w-full h-[560px] rounded-lg border border-[#e3e0d3] bg-white" frameBorder="0" />
            <button type="button" onClick={() => setMode("form")} data-testid="switch-to-enquiry-form"
              className="text-sm text-[#51637a] underline underline-offset-4 hover:text-[#0d2f4d] transition-colors mx-auto">
              Prefer we call you back? Leave your details instead
            </button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-[#0d2f4d]">Let's work out what's right for you</DialogTitle>
              <DialogDescription className="text-[#51637a]">Two minutes, no commitment. A recruiter will call you back.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3.5">
              <div><label className={labelCls}>Your name</label>
                <input required value={form.name} onChange={set("name")} data-testid="enquiry-name" className={inputCls} /></div>
              <div><label className={labelCls}>Work email</label>
                <input required type="email" value={form.email} onChange={set("email")} data-testid="enquiry-email" className={inputCls} /></div>
              <div><label className={labelCls}>Company name</label>
                <input required value={form.company_name} onChange={set("company_name")} data-testid="enquiry-company" className={inputCls} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Roles you hire / year</label>
                  <select value={form.roles_per_year} onChange={set("roles_per_year")} data-testid="enquiry-roles-per-year" className={inputCls}>
                    <option value="">Select...</option>
                    <option>1–2</option><option>3–6</option><option>7–12</option><option>12+</option>
                  </select></div>
                <div><label className={labelCls}>Preferred contact</label>
                  <select value={form.contact_method} onChange={set("contact_method")} data-testid="enquiry-contact-method" className={inputCls}>
                    <option value="">Select...</option>
                    <option>Phone call</option><option>Email</option><option>Video call</option>
                  </select></div>
              </div>
              <div><label className={labelCls}>Biggest hiring challenge right now (optional)</label>
                <textarea rows={2} value={form.message} onChange={set("message")} data-testid="enquiry-message" className={inputCls} /></div>
              <button type="submit" disabled={busy} data-testid="enquiry-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0d2f4d] text-white font-medium text-sm py-3.5 hover:bg-[#1c4d78] transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <PhoneCall size={15} />}
                {busy ? "Sending..." : "Book my call"}
              </button>
              <button type="button" onClick={() => setMode("calendly")} data-testid="switch-to-calendly"
                className="w-full text-sm text-[#51637a] underline underline-offset-4 hover:text-[#0d2f4d] transition-colors">
                Or pick a time in the calendar instead
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
